import React, { memo, useCallback, useMemo, useState } from 'react'

import { Datepicker, DatepickerValueType, FormOnChange } from '@procsea/design-system'

import { OrderType } from 'src/types'

import { getMaxDate, getTypeFromDate } from '../../orders/utils'
import OrderTypeSelector, { OrderTypeSelectorValue } from '../OrderTypeSelector/OrderTypeSelector'
import { ShippingDateRange, getDateFromType, getFormattedRange } from './utils'

interface ShippingDateRangeSelectorProps {
  dateLabel?: string
  from?: Date
  futureOrdersCount?: number
  name?: string
  onChange: FormOnChange<ShippingDateRange>
  periodLabel?: string
  portalId?: string
  to?: Date
  todayOrdersCount?: number
}

const ShippingDateRangeSelector = ({
  dateLabel,
  from,
  futureOrdersCount,
  name,
  onChange,
  periodLabel,
  portalId,
  to,
  todayOrdersCount,
}: ShippingDateRangeSelectorProps) => {
  const [shippingDateRange, setShippingDateRange] = useState<DatepickerValueType<true>>()

  const formattedRange = useMemo(() => getFormattedRange({ from, to }), [from, to])
  const type = useMemo(
    () => getTypeFromDate(shippingDateRange ?? [null, null]),
    [shippingDateRange]
  )

  if (formattedRange !== shippingDateRange) {
    setShippingDateRange(formattedRange)
  }

  const handleTypeChange: FormOnChange<string | Id> = useCallback(
    ({ value }) => {
      const range = getDateFromType(value as OrderTypeSelectorValue)

      setShippingDateRange(
        getFormattedRange({ from: range.fromShippingDate, to: range.toShippingDate })
      )
      onChange({ value: range })
    },
    [onChange]
  )

  const handleShippingDateChange: FormOnChange<DatepickerValueType<true>> = useCallback(
    ({ value }) => {
      const [fromShippingDate, toShippingDate] = value
      const newType = getTypeFromDate(value)

      setShippingDateRange(
        getFormattedRange({ from: fromShippingDate ?? undefined, to: toShippingDate ?? undefined })
      )

      if (newType !== OrderType.PAST || !!toShippingDate) {
        onChange({
          value: {
            fromShippingDate: fromShippingDate ?? undefined,
            toShippingDate: toShippingDate ?? undefined,
          },
        })
      }
    },
    [onChange]
  )

  return (
    <>
      <OrderTypeSelector
        futureOrdersCount={futureOrdersCount}
        name={name}
        onChange={handleTypeChange}
        label={periodLabel}
        todayOrdersCount={todayOrdersCount}
        value={type}
      />

      <Datepicker<true>
        data-e2e="shipping-datepicker-range"
        label={dateLabel}
        maxDate={getMaxDate(from ?? undefined)}
        onChange={handleShippingDateChange}
        placeholder={gettext('Shipping date')}
        portalId={portalId}
        value={shippingDateRange}
        withRange
      />
    </>
  )
}

export default memo(ShippingDateRangeSelector)
